<template>
  <div
    id="ingredients"
    ref="ingredientsEl"
    class="md:h-full bg-blue-dark px-4 md:px-8 lg:p-16 py-8"
  >
    <!-- Title -->
    <h2 class="text-3xl mb-8 text-white">
      {{ title }}
    </h2>

    <!-- Portion selector -->
    <div
      v-if="portions"
      class="flex bg-white rounded-lg mb-6"
    >
      <button
        v-if="ingredients.amountIsScalable"
        class="w-12 h-12 border-2 border-outline-grey ltr:rounded-l-lg rtl:rounded-r-lg"
        data-gtm-trigger="click"
        data-action="decrease"
        @click="performStep('down')"
      >
        <IconMinus
          class="icon m-auto"
          width="16px"
        />
      </button>
      <div
        class="flex-1 flex items-center h-12 border-t-2 border-b-2 border-outline-grey"
        :class="{'border-2 rounded-lg': !ingredients.amountIsScalable}"
      >
        <span class="amount-of-portions text-xs mx-auto">{{ portions }} {{ amountText }}</span>
      </div>
      <button
        v-if="ingredients.amountIsScalable"
        class="w-12 h-12 border-2 border-outline-grey ltr:rounded-r-lg rtl:rounded-l-lg"
        data-gtm-trigger="click"
        data-action="increase"
        @click="performStep('up')"
      >
        <IconPlus
          class="icon text-black m-auto"
          width="16px"
        />
      </button>
    </div>

    <!-- Ingredient list -->
    <div
      v-for="ingredientGroup in scaledIngredientsGroups"
      :key="ingredientGroup.index"
    >
      <h3 v-if="ingredientGroup.name" class="text-3xl mb-8 text-white">
        {{ ingredientGroup.name }}
      </h3>

      <ul class="recipe-ingredients text-md bg-white rounded-lg mb-10 border-outline-grey border-2">
        <li
          v-for="ingredient in ingredientGroup.items"
          :key="ingredient.id"
          class="flex items-center justify-between p-4 border-t-2 border-outline-grey first:border-t-0"
        >
          <input
            :id="ingredient.uniqueId"
            class="ltr:mr-0 rtl:ml-0 order-1 rtl:order-first"
            :name="ingredientGroup.name"
            type="checkbox"
            @change="trackIngredientTick(ingredient.name.value.ingredientName, $event.target.checked)"
          >
          <label
            :for="ingredient.uniqueId"
            class="flex-1 lorder-first flex items-center"
          >
            <div class="flex w-full items-center">
              <a
                v-if="ingredient.product"
                :href="ingredient.product.url"
                class="flex w-full items-center underline hover:no-underline ltr:pr-2 rtl:pl-2"
              >

                <span class="ltr:mr-2 rtl:ml-2">
                  {{ ingredient.name.value.ingredientUnitName }}{{ ingredient.secondaryUnit.value ? ` (${ingredient.secondaryUnit.value})` : '' }} {{ capitalize(ingredient.name.value.ingredientName) }}
                </span>
                <Picture
                  v-bind="ingredient.product.image"
                  :mobile="[48, 48]"
                  width="48"
                  height="48"
                  img-class="m-auto"
                  class="w-16 h-16 content-center flex-shrink-0  ltr:ml-auto rtl:mr-auto"
                />
              </a>
              <span
                v-else
                class="ltr:pr-2 rtl:pl-2"
              >
                {{ ingredient.name.value.ingredientUnitName }}{{ ingredient.secondaryUnit.value ? ` (${ingredient.secondaryUnit.value})` : '' }} {{ capitalize(ingredient.name.value.ingredientName) }}
              </span>
            </div>
          </label>
        </li>
      </ul>
    </div>
  </div>
</template>

<script>
import Picture from '@/components/Picture.vue'
import IconMinus from '@/assets/icons/minus.svg'
import IconPlus from '@/assets/icons/plus.svg'
import ingredientScaleHelper from '@/compositions/recipes/RecipeIngrediensScaleHelper'
import {
  computed, onMounted, onBeforeUnmount, ref
} from 'vue'

export default {
  components: {
    Picture,
    IconMinus,
    IconPlus
  },
  props: {
    ingredients: {
      type: Object,
      required: true
    },
    title: {
      type: String,
      default: ''
    },
    language: {
      type: String,
      default: ''
    },
    portionSingularText: {
      type: String,
      default: 'portion'
    },
    portionPluralText: {
      type: String,
      default: 'portions'
    }
  },
  setup(props) {
    const { setPortions, state } = ingredientScaleHelper(props.ingredients, 'en', props.portionSingularText, props.portionPluralText)

    const minValue = 1
    const maxValue = state.basePortions * 10
    const stepValue = 1

    const ingredientsEl = ref(null)
    const firedEvents = new Set()
    let visibilityTimeout = null

    function performStep(action) {
      let newValue
      if (action === 'up') {
        newValue = Math.floor((state.portions + stepValue) / stepValue) * stepValue
      } else {
        newValue = Math.ceil((state.portions - stepValue) / stepValue) * stepValue
      }

      if (action === 'up' && newValue <= maxValue) {
        setPortions(Math.floor((state.portions + stepValue) / stepValue) * stepValue)
      } else if (action === 'down' && newValue >= minValue) {
        setPortions(Math.ceil((state.portions - stepValue) / stepValue) * stepValue)
      }
    }
    function capitalize(name) {
      return name.charAt(0).toUpperCase() + name.slice(1)
    }
    function trackIngredientTick(name, checked) {
      if (checked) {
        window.dataLayer.push({
          event: 'checkIngredient',
          ingredientName: name
        })
      }
    }

    function handleVisibility(entries) {
      const [entry] = entries
      if (entry.isIntersecting && !firedEvents.has('viewRecipeIngredients10s')) {
        visibilityTimeout = setTimeout(() => {
          if (!firedEvents.has('viewRecipeIngredients10s')) {
            window.dataLayer.push({
              event: 'viewRecipeIngredients10s'
            })
            firedEvents.add('viewRecipeIngredients10s')
          }
        }, 10000)
      } else if (!entry.isIntersecting && visibilityTimeout) {
        clearTimeout(visibilityTimeout)
      }
    }

    onMounted(() => {
      const observer = new IntersectionObserver(handleVisibility, { threshold: 0.5 })
      if (ingredientsEl.value) {
        observer.observe(ingredientsEl.value)
      }
    })

    onBeforeUnmount(() => {
      if (visibilityTimeout) {
        clearTimeout(visibilityTimeout)
      }
    })

    return {
      ingredientsEl,
      minValue,
      maxValue,
      stepValue,
      portions: computed(() => (props.ingredients.amountIsScalable ? state.portions : ' ')),

      amountText: computed(() => state.amountText),
      performStep,
      scaledIngredientsGroups: computed(() => state.ingredientGroups),

      capitalize,
      trackIngredientTick
    }
  }
}
</script>
